/** @file sheet.pack: read-only; plans packed sheets for the entries that hold loose tile images (R6). */
import { ERROR_CODES } from '@zilo/pose3d-format/errors/codes';
import { slotKey } from '@zilo/pose3d-format/derive/slotKey';
import { tileCountOf } from '@zilo/pose3d-format/tables/tileCountOf';
import type { FacetEntry } from '@zilo/pose3d-format/types/facet';
import type { Puppet } from '@zilo/pose3d-format/types/puppet';
import type { Warning } from '@zilo/pose3d-format/types/warning';
import { checkArray } from '@zilo/pose3d-format/validate/fields/checkArray';
import { checkString } from '@zilo/pose3d-format/validate/fields/checkString';
import { checkTuple } from '@zilo/pose3d-format/validate/fields/checkTuple';
import { throwError } from '@zilo/pose3d-std/errors/throwError';
import type { CommandDef } from '../apply/commandDef';
import type { CommandContext } from '../context/commandContext';
import type { PackInput } from '@zilo/pose3d-image-io/pack/packShelves';
import { requireSectorTable } from '../facet/requireSectorTable';
import { compact } from '../validate/compact';
import { readInput } from '../validate/readInput';
import type { PackPlan } from './packPlan';

type Block = { id: string; drawable: string; index: number; entry: FacetEntry; tiles: number };

/** Entries holding a loose tile image, by `entry.sheet`; partly filled ones are skipped with `EMPTY_SLOT`. */
function collect(p: Puppet, ctx: CommandContext, only: string[] | undefined, warnings: Warning[]): { groups: Map<string, Block[]>; skipped: string[] } {
  const groups = new Map<string, Block[]>();
  const skipped: string[] = [];
  p.drawables.forEach((d, di) => {
    if (d.kind !== 'impostor') return;
    d.facets.forEach((entry, index) => {
      const sheetImage = p.sheets[entry.sheet]?.image;
      const loose = Object.values(entry.slots).some((img) => img !== sheetImage);
      if (!loose || (only !== undefined && !only.includes(entry.sheet))) return;
      const tiles = tileCountOf(requireSectorTable(p, entry.yaw, ctx.sectorTables));
      const empty = Array.from({ length: tiles }, (_, t) => t).filter((t) => !Object.hasOwn(entry.slots, String(t)));
      const id = `${d.id}/${index}`;
      if (empty.length > 0) {
        skipped.push(id);
        warnings.push({ code: 'EMPTY_SLOT', path: `/puppet/drawables/${di}/facets/${index}/slots`, params: { entry: id, empty },
          message: `entry ${id} has empty tiles (${empty.join(', ')}); it is not packed`, hint: 'slot.fill the empty tiles, then sheet.pack again' });
        return;
      }
      groups.set(entry.sheet, [...(groups.get(entry.sheet) ?? []), { id, drawable: d.id, index, entry, tiles }]);
    });
  });
  return { groups, skipped };
}

function planOf(group: string, blocks: Block[], ctx: CommandContext, padding: number, max: [number, number]): PackPlan {
  const inputs: PackInput[] = blocks.map((b) => {
    const [w, h] = b.entry.size;
    return b.entry.extendX === true ? { id: b.id, w, h: h * b.tiles } : { id: b.id, w: w * b.tiles, h };
  });
  const packed = ctx.packShelves(inputs, { padding });
  if (packed.width > max[0] || packed.height > max[1]) {
    throwError(ERROR_CODES.LIMIT, { size: [packed.width, packed.height], maxSize: max }, { path: '/input/maxSize', hint: 'pack fewer sheets at once or raise maxSize' });
  }
  const plan: PackPlan = { sheet: `packed-${group}`, size: [packed.width, packed.height], cells: {}, sources: {} };
  for (const b of blocks) {
    const rect = packed.rects.find((r) => r.id === b.id);
    for (let t = 0; rect !== undefined && t < b.tiles; t++) {
      const key = slotKey(b.drawable, b.index, t);
      plan.cells[key] = b.entry.extendX === true ? [rect.x, rect.y + t * b.entry.size[1]] : [rect.x + t * b.entry.size[0], rect.y];
      plan.sources[key] = b.entry.slots[String(t)] ?? '';
    }
  }
  return plan;
}

/** `sheet.pack { sheets?, maxSize?, padding? }` → `{ sheets: PackPlan[], skipped }` (padding 1, max 4096²); changes nothing. */
export const sheetPackCommand: CommandDef<'sheet.pack'> = {
  name: 'sheet.pack',
  readOnly: true,
  /** Checks the raw input (VALIDATION under the input path). */
  validate: (input, path) => {
    const r = readInput(input, path, ['sheets', 'maxSize', 'padding']);
    return compact({
      sheets: r.opt('sheets', (k) => r.check(k, checkArray(checkString())) as string[]),
      maxSize: r.opt('maxSize', (k) => r.check(k, checkTuple(2, { int: true, min: 1 })) as [number, number]),
      padding: r.opt('padding', (k) => r.int(k, { min: 0 })),
    });
  },
  /** Reads the draft and returns the plans; the inverse is empty. */
  apply: (draft, input, ctx) => {
    const warnings: Warning[] = [];
    const { groups, skipped } = collect(draft.puppet, ctx, input.sheets, warnings);
    const sheets = [...groups].map(([group, blocks]) => planOf(group, blocks, ctx, input.padding ?? 1, input.maxSize ?? [4096, 4096]));
    return { result: { sheets, skipped }, warnings, inverse: [] };
  },
};
